import React, { useState, useEffect, useRef } from 'react'
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material'
import { propOr, isEmpty } from 'ramda'
import SaveIcon from '@mui/icons-material/Save'
import SettingsBackupRestoreSharpIcon from '@mui/icons-material/SettingsBackupRestoreSharp'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useSnackbar } from 'notistack'
import axios from 'services/customAxios'
import { getItem } from 'utils/localstorage'
import { getEndpoints } from 'constants/endpoints'
import { getDatesForCurrentWeek, isInPast, isTodayDate } from 'utils/date'
import { styled } from '@mui/material/styles'
import InputBase from '@mui/material/InputBase'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}))

const DailyCapacitiesView = ({ dailyCapacitiesData }) => {
  const {
    yards = [],
    capacities = {},
    transporterDefaultCapacities = {},
  } = dailyCapacitiesData
  const [currentWeek, setCurrentWeek] = useState(0)
  const [capacityChange, setCapacityChange] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const [initialCapacityObj, setInitialCapacityObj] = useState({})

  //updated capacity object
  const [capacityObj, setCapacityObj] = useState({})

  //dates to display in header
  const { dates, modifiedDates } = getDatesForCurrentWeek(
    new Date(),
    'dd/MM',
    true,
  )

  const createCapacityInfo = () => {
    const result = {}
    yards?.forEach((yard) => {
      result[yard] = []
      modifiedDates.forEach((date) => {
        let capacityValue = 0
        if (capacities[yard]) {
          capacities[yard].forEach((capacity) => {
            if (capacity?.capacity_date.slice(0, 10) === date) {
              capacityValue = capacity.capacity_value
            }
          })
        }
        result[yard].push({
          facility_id: yard,
          capacity_type_code: 'CPRTC',
          capacity_value: capacityValue,
          capacity_date: date,
        })
      })
    })
    setCapacityObj(result)
    setInitialCapacityObj(JSON.parse(JSON.stringify(result)))
  }

  useEffect(() => {
    createCapacityInfo()
  }, [dailyCapacitiesData])

  const handleCapacitiesSave = async () => {
    const { vendor_id: vendorId } = getItem('userData')
    const capacitiesEndPoint = getEndpoints({ vendorId }).capacities
    const payload = { daily_capacities: capacityObj }
    const response = await axios.post(capacitiesEndPoint, payload)
    if (response.status === 200) {
      enqueueSnackbar('capacities saved successfully', {
        variant: 'success',
      })
      setCapacityChange(false)
    } else {
      enqueueSnackbar('Unable to save capacities', {
        variant: 'error',
      })
    }
  }

  const handleCapacitiesReset = () => {
    setCapacityObj(initialCapacityObj)
    setCapacityChange(false)
  }

  const handleCapacityChange = (value, facilityId, index, maxVal) => {
    if (
      value <= maxVal ||
      (value == initialCapacityObj[facilityId][index]?.capacity_value - 1 &&
        value > maxVal)
    ) {
      const updatedCapacityObj = { ...capacityObj }
      const facility = updatedCapacityObj[facilityId]
      const updatedFacility = {
        ...facility[index],
        capacity_value:
          parseInt(value) < 0 || isEmpty(value)
            ? 0
            : value < initialCapacityObj[facilityId][index].capacity_value &&
              value > maxVal
            ? maxVal
            : parseInt(value),
      }
      const updatedFacilities = [
        ...facility.slice(0, index),
        updatedFacility,
        ...facility.slice(index + 1),
      ]
      updatedCapacityObj[facilityId] = updatedFacilities
      setCapacityObj(updatedCapacityObj)
      setCapacityChange(true)
    } else {
      enqueueSnackbar(
        `Capacity cannot exceed the yard's maximum capacity of ${maxVal}`,
        {
          variant: 'error',
        },
      )
    }
  }

  return isEmpty(yards) ? (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <h3> No yard is associated with this vendor</h3>
    </div>
  ) : (
    <>
      <div
        className="rowStack"
        style={{ display: 'flex', justifyContent: 'flex-end' }}
      >
        <Tooltip title="Previous week">
          <span>
            <IconButton
              onClick={() => {
                if (currentWeek === 1) setCurrentWeek(currentWeek - 1)
              }}
              disabled={!(currentWeek === 1)}
            >
              <KeyboardArrowLeftIcon style={{ fontSize: '2rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next week">
          <span>
            <IconButton
              onClick={() => {
                if (currentWeek === 0) setCurrentWeek(currentWeek + 1)
              }}
              disabled={!(currentWeek === 0)}
            >
              <KeyboardArrowRightIcon style={{ fontSize: '2rem' }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <TableContainer style={{ overflow: 'auto' }}>
        <MuiTable stickyHeader aria-label="sticky table">
          {Object.keys(capacityObj).length > 0 && (
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    backgroundColor: '#e8edf6',
                    color: '#3c3d40',
                    border: '1px solid #fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    width: '160px',
                    whiteSpace: 'nowrap',
                  }}
                ></TableCell>
                {dates
                  .slice(0 + currentWeek * 5, 5 + currentWeek * 5)
                  .map((date, index) => (
                    <TableCell
                      key={`schedule-date-${index}`}
                      style={{
                        backgroundColor: '#e8edf6',
                        color: '#3c3d40',
                        border: '1px solid #fff',
                        fontWeight: 800,
                        fontSize: '16px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div className="dateStyle">{date}</div>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {Object.keys(capacityObj).map((facilityId) => (
              <StyledTableRow>
                <TableCell style={{ fontWeight: 800, fontSize: '14px' }}>
                  {facilityId}
                </TableCell>
                {capacityObj[facilityId]
                  .slice(0 + currentWeek * 5, 5 + currentWeek * 5)
                  .map(({ capacity_value, capacity_date }, index) => (
                    <TableCell key={`schedule-date-${index}`}>
                      <InputBase
                        type="number"
                        value={capacity_value}
                        onChange={(event) =>
                          handleCapacityChange(
                            event.target.value,
                            facilityId,
                            index + currentWeek * 5,
                            propOr(
                              100,
                              facilityId,
                              transporterDefaultCapacities,
                            ),
                          )
                        }
                        style={{
                          width: '100%',
                          height: '100%',
                          textAlign: 'center',
                          border: 'none',
                          outline: 'none',
                        }}
                        // inputProps={{
                        //   max: propOr(
                        //     100,
                        //     facilityId,
                        //     transporterDefaultCapacities,
                        //   ),
                        // }}
                        disabled={
                          isInPast(capacity_date) && !isTodayDate(capacity_date)
                        }
                      />
                    </TableCell>
                  ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {capacityChange && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'fixed',
            bottom: '20px',
            right: '20px',
          }}
        >
          <Button
            variant="contained"
            style={{ color: 'white', fontSize: '1rem', marginRight: '4px' }}
            startIcon={
              <SettingsBackupRestoreSharpIcon style={{ fontSize: '1.7rem' }} />
            }
            onClick={handleCapacitiesReset}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: 'red', color: 'white', fontSize: '1rem' }}
            startIcon={<SaveIcon style={{ fontSize: '1.7rem' }} />}
            onClick={handleCapacitiesSave}
          >
            Save
          </Button>
        </div>
      )}
    </>
  )
}

export default DailyCapacitiesView
